import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn, getInitials } from '@/lib/utils'
import { Settings, X } from 'lucide-react'
import { useAuthStore } from '@/stores/auth-store'
import { Button } from '@/components/ui/button'
import { getMenuItems } from './menu-config'
import { useOrganizationStore } from '@/stores/organization-store'
import { useAtendimento } from '@/hooks/use-atendimento'
import { SpartanHelmet } from '@/components/icons/spartan-helmet'

interface MobileSidebarProps {
  open: boolean
  onClose: () => void
}

export function MobileSidebar({ open, onClose }: MobileSidebarProps) {
  const { t } = useTranslation('common')
  const location = useLocation()
  const { user } = useAuthStore()
  const { currentOrganization } = useOrganizationStore()
  const { temModulo } = useAtendimento(currentOrganization?.id)
  const menuItems = getMenuItems(t).filter(
    (item) => !item.requiresAtendimento || temModulo === true
  )

  const handleLinkClick = () => {
    onClose()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <aside
        className={cn(
          'absolute left-0 top-0 h-full w-64 border-r border-gray-800/50 flex flex-col',
          'transform transition-transform duration-300 ease-out translate-x-0'
        )}
        style={{ backgroundColor: '#0d0d14' }}
      >
        {/* Header with Logo and Close */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800/50">
          <Link to="/dashboard" className="flex items-center gap-3" onClick={handleLinkClick}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-glow-primary">
              <SpartanHelmet className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              ARCHON
            </span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-gray-400 hover:text-white hover:bg-white/5"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto scrollbar-thin">
          {menuItems.map((item, indice, visiveis) => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path))
            const abreGrupo =
              !!item.group && item.group !== visiveis[indice - 1]?.group

            return (
              <div key={`grupo-${item.id}`}>
              {abreGrupo && (
                <p className="px-3 pb-1 pt-4 text-xs font-semibold uppercase tracking-wider text-gray-600">
                  {item.group}
                </p>
              )}
              <Link
                to={item.path}
                onClick={handleLinkClick}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-3 rounded-xl',
                  'transition-all duration-200 group relative',
                  isActive
                    ? 'bg-indigo-500/20 text-indigo-300'
                    : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                )}
              >
                {isActive && (
                  <div className="absolute left-0 w-1 h-8 bg-indigo-500 rounded-r-full" />
                )}
                <item.icon
                  className={cn(
                    'w-5 h-5 flex-shrink-0',
                    isActive && 'text-indigo-400'
                  )}
                />
                <span className="font-medium truncate">{item.label}</span>
                {item.badge && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center ml-auto px-2">
                    {item.badge}
                  </span>
                )}
              </Link>
              </div>
            )
          })}
        </nav>

        {/* Settings */}
        <div className="px-3 pb-2">
          <Link
            to="/settings"
            onClick={handleLinkClick}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-3 rounded-xl',
              'text-gray-400 hover:bg-white/5 hover:text-gray-200 transition-colors'
            )}
          >
            <Settings className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">{t('nav.settings')}</span>
          </Link>
        </div>

        {/* User */}
        <div className="p-4 border-t border-gray-800/50">
          <Link
            to="/profile"
            onClick={handleLinkClick}
            className="flex items-center gap-3 rounded-xl p-2 -m-2 transition-colors hover:bg-white/5"
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-white">
                {getInitials(user?.full_name, user?.email)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate text-gray-200">
                {user?.full_name || t('header.user')}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </Link>
        </div>
      </aside>
    </div>
  )
}
